import { createContext, useContext, useState, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useParams } from "react-router-dom";
import { baseUrl } from "../utils/Const";

const PublicOrderContext = createContext();

export const PublicOrderProvider = ({ children }) => {
  const { slug } = useParams();
  const ns = slug ? `_${slug}` : "";

  const socketRef = useRef(null);
  const isSyncingFromSocket = useRef(false);
  const [participantsCount, setParticipantsCount] = useState(1);

  const [orderInfo, setOrderInfo] = useState(() => {
    const stored = localStorage.getItem(`orderInfo${ns}`);
    return stored ? JSON.parse(stored) : null;
  });

  const [orderId, setOrderId] = useState(() => {
    return localStorage.getItem(`orderId${ns}`) || null;
  });

  const [cart, setCart] = useState(() => {
    const stored = localStorage.getItem(`cart${ns}`);
    return stored ? JSON.parse(stored) : [];
  });

  const [restaurant, setRestaurant] = useState(() => {
    const stored = localStorage.getItem(`restaurant${ns}`);
    return stored ? JSON.parse(stored) : null;
  });

  const [clientId] = useState(() => {
    let id = sessionStorage.getItem("group_order_client_id");
    if (!id) {
      id = "client_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem("group_order_client_id", id);
    }
    return id;
  });

  // 1. Manage WebSocket Connection & Room Lifecycle
  useEffect(() => {
    if (!orderId) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setParticipantsCount(1);
      return;
    }

    console.log(`[Socket.io] Connecting for group order room: ${orderId}...`);
    const socket = io(baseUrl, {
      transports: ["websocket"]
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      console.log(`[Socket.io] Connected. Joining group order room: ${orderId}`);
      socket.emit("join-group-order", { orderId, clientId });
    });

    socket.on("cart-updated", (newCart) => {
      console.log("[Socket.io] Cart update received from group:", newCart);
      isSyncingFromSocket.current = true;
      setCart(newCart);
    });

    socket.on("participants-changed", ({ count }) => {
      console.log(`[Socket.io] Active group participants: ${count}`);
      setParticipantsCount(count);
    });

    socket.on("disconnect", () => {
      console.log("[Socket.io] Disconnected from sync server.");
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [orderId, clientId]);

  // 2. Emit Cart changes to other group participants
  useEffect(() => {
    if (isSyncingFromSocket.current) {
      isSyncingFromSocket.current = false;
      return;
    }

    if (orderId && socketRef.current && socketRef.current.connected) {
      console.log("[Socket.io] Emitting cart update to group:", cart);
      socketRef.current.emit("update-cart", { orderId, cart, senderId: clientId });
    }
  }, [cart, orderId, clientId]);


  useEffect(() => {
    localStorage.setItem(`orderInfo${ns}`, JSON.stringify(orderInfo));
  }, [orderInfo, ns]);

  useEffect(() => {
    if (orderId) {
      localStorage.setItem(`orderId${ns}`, orderId);
    } else {
      localStorage.removeItem(`orderId${ns}`);
    }
  }, [orderId, ns]);

  useEffect(() => {
    localStorage.setItem(`cart${ns}`, JSON.stringify(cart));
  }, [cart, ns]);

  useEffect(() => {
    localStorage.setItem(`restaurant${ns}`, JSON.stringify(restaurant));
  }, [restaurant, ns]);

  const clearOrderDetails = () => {
    setOrderInfo(null);
    setOrderId(null);
    setCart([]);
    setRestaurant(null);
    localStorage.removeItem(`restaurant${ns}`);
    localStorage.removeItem(`orderInfo${ns}`);
    localStorage.removeItem(`orderId${ns}`);
    localStorage.removeItem(`cart${ns}`);
  };

  /* ================= CART FUNCTIONS ================= */

  const addToCart = (product, selectedVariant = null, selectedAddOns = []) => {
    const variantKey = selectedVariant ? selectedVariant.name : "";
    const addOnsKey = (selectedAddOns || []).map((a) => a.name).sort().join(",");
    const cartKey = `${product._id || product.id}-${variantKey}-${addOnsKey}`;
    const unitPrice = (selectedVariant ? Number(selectedVariant.price) : Number(product.price)) + (selectedAddOns || []).reduce((sum, a) => sum + Number(a.price), 0);

    setCart((prev) => {
      const existing = prev.find((p) => p.cartKey === cartKey);

      if (existing) {
        return prev.map((p) =>
          p.cartKey === cartKey
            ? { ...p, qty: p.qty + 1 }
            : p
        );
      }

      return [
        ...prev,
        {
          cartKey,
          id: product._id || product.id,
          productName: product.productName,
          price: unitPrice,
          basePrice: product.price,
          qty: 1,
          selectedVariant,
          selectedAddOns,
        },
      ];
    });
  };

  const increaseQty = (item) => {
    setCart((prev) =>
      prev.map((p) =>
        p.cartKey === item.cartKey ? { ...p, qty: p.qty + 1 } : p
      )
    );
  };

  const decreaseQty = (item) => {
    setCart((prev) =>
      prev
        .map((p) =>
          p.cartKey === item.cartKey
            ? { ...p, qty: p.qty - 1 }
            : p
        )
        .filter((p) => p.qty > 0)
    );
  };

  const updateItemQty = (cartKey, qty) => {
    setCart((prev) =>
      prev.map((p) =>
        p.cartKey === cartKey
          ? { ...p, qty: Math.max(1, qty) }
          : p
      )
    );
  };

  const removeItem = (item) => {
    setCart((prev) =>
      prev.filter((p) => p.cartKey !== item.cartKey)
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce(
      (sum, item) => sum + item.price * item.qty,
      0
    );
  };

  /* ================= RESET SESSION ================= */

  const resetSession = () => {
    setOrderInfo(null);
    setOrderId(null);
    setCart([]);

    localStorage.removeItem(`orderInfo${ns}`);
    localStorage.removeItem(`orderId${ns}`);
    localStorage.removeItem(`cart${ns}`);
    sessionStorage.removeItem("table_lock_active");
  };


  return (
    <PublicOrderContext.Provider
      value={{
        orderInfo,
        setOrderInfo,

        orderId,
        setOrderId,

        cart,
        addToCart,
        increaseQty,
        decreaseQty,
        updateItemQty,
        removeItem,
        clearCart,
        getTotal,

        resetSession,
        restaurant,
        setRestaurant,
        participantsCount,
      }}
    >
      {children}
    </PublicOrderContext.Provider>
  );
};

export const usePublicOrder = () =>
  useContext(PublicOrderContext);
